import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import type { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { describeFailure } from '@/utils/fulfillmentErrors';
import { showGlobalToast } from '@/pages/fulfillment/components/gtoast';
import { ULTIMATE_MODAL } from '@/pages/fulfillment/components/ultimateModal';
import '../../ultimateDesign.css';

interface SampleSetModalProps {
  readonly show: boolean;
  readonly client: FulfillmentAdminClient;
  readonly onHide: () => void;
}

/** Counts sit in running text here, and "1 items" reads as a bug in the screen rather than in the set. */
const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

export const parseSampleSkus = (value: string): { skus: Array<{ sku: string; qty: number }>; valid: boolean } => {
  const entries = value
    .split(/[,\n]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  const skus: Array<{ sku: string; qty: number }> = [];
  for (const item of entries) {
    const parts = item.split(':').map((part) => part.trim());
    const [sku, qty = '1'] = parts;
    const parsedQty = Number(qty);
    if (parts.length > 2 || !sku || !Number.isInteger(parsedQty) || parsedQty <= 0) {
      return { skus: [], valid: false };
    }
    skus.push({ sku, qty: parsedQty });
  }
  // An empty box is a valid set: clearing it is how assignment stops without cancelling a window.
  return { skus, valid: true };
};

/**
 * The one sample set every open assignment window gives away (SSD-7448). It sits under Sample assignment
 * beside the windows it feeds; it used to live on the Marketing & Samples screen, away from them.
 */
export function SampleSetModal({ show, client, onHide }: SampleSetModalProps) {
  const [skus, setSkus] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const parsed = parseSampleSkus(skus);

  useEffect(() => {
    if (!show) {
      return;
    }
    let live = true;
    setLoaded(false);
    setLoadError(null);
    setSaveError(null);
    client.getSampleSet().then(
      (response) => {
        if (live) {
          setSkus(response.skus.map((item) => `${item.sku}:${item.qty}`).join(', '));
          setLoaded(true);
        }
      },
      (error: unknown) => {
        if (live) {
          setLoadError(describeFailure(error, 'Could not load the sample set.'));
        }
      },
    );
    return () => {
      live = false;
    };
  }, [show, client]);

  // A save on its way would close whatever modal is open when it lands, so the modal holds until it has.
  const hide = () => {
    if (!busy) {
      onHide();
    }
  };

  const save = async () => {
    if (!parsed.valid) {
      return;
    }
    setBusy(true);
    setSaveError(null);
    try {
      await client.updateSampleSet(parsed.skus);
      showGlobalToast(
        parsed.skus.length > 0
          ? `Sample set saved — every open window now gives ${plural(parsed.skus.length, 'item')}.`
          : 'Sample set cleared — open windows assign nothing until a set is saved.',
      );
      onHide();
    } catch (error) {
      // The server names the sku it could not find, which is the one thing the desk needs to fix.
      setSaveError(describeFailure(error, 'The server refused the sample set.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={hide} keyboard={!busy} aria-labelledby="sample-set-title" {...ULTIMATE_MODAL}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title id="sample-set-title">Edit sample set</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadError && <Alert variant="danger">{loadError}</Alert>}
        {saveError && <Alert variant="danger">{saveError}</Alert>}
        <Form.Group controlId="sample-skus">
          <Form.Label>Sample set (SKU:qty)</Form.Label>
          <Form.Control value={skus} disabled={busy || !loaded} onChange={(event) => setSkus(event.target.value)} />
          {skus.trim() && !parsed.valid && (
            <Form.Text className="text-danger d-block">Enter each item as SKU or SKU:positive integer.</Form.Text>
          )}
          {/* One set for every open window: the window opens a period, this decides what it gives away. */}
          <Form.Text className="text-muted d-block">
            Every open assignment window gives away this set. Clearing it stops assignment without cancelling a window.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={hide}>
          Close
        </Button>
        <Button disabled={busy || !loaded || !parsed.valid} onClick={() => void save()}>
          Save set
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
